import { Bars3Icon } from '@heroicons/react/24/outline';

import IconButton from '../components/IconButton';
import Text from '../components/Text';
import { useNombreConversationsNonLues } from '../api/useNombreConversationsNonLues';
import { t } from '../i18n/langue';

/**
 * En-tête d'écran : le titre, et le bouton qui ouvre le panneau.
 *
 * Le bouton est **toujours** présent (#17 §3). Même à un seul écran éligible, où
 * le panneau n'a pas de liste à montrer, il reste la seule porte vers le compte,
 * l'établissement sélectionné et la déconnexion. Le masquer économiserait un
 * bouton et coûterait la déconnexion.
 *
 * Il porte aussi le compteur de conversations non lues : un compte à la seule
 * messagerie n'a pas de liste d'écrans dans le panneau, donc pas d'autre place
 * visible pour la pastille. Hors messagerie, le compteur vaut 0 et rien ne s'affiche.
 */
interface EnteteAppProps {
    titre: string;
    onOuvrirPanneau: () => void;
    /** Bouton propre à l'écran, posé à droite (retour de conversation, action…). */
    action?: React.ReactNode;
}

export default function EnteteApp({ titre, onOuvrirPanneau, action }: EnteteAppProps) {
    const nonLues = useNombreConversationsNonLues();
    const libelle =
        nonLues === 0
            ? t('coque.ouvrirNavigation')
            : t('coque.ouvrirNavigationNonLues', { n: nonLues });

    return (
        <header className="flex items-center gap-2 border-b border-border-color-light bg-white px-3 py-2">
            <IconButton libelle={libelle} onPress={onOuvrirPanneau}>
                <span className="relative">
                    <Bars3Icon className="h-6 w-6 text-text-color" />
                    {nonLues > 0 && (
                        <span
                            aria-hidden="true"
                            className="absolute -right-2 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-main-color px-1 text-xs font-semibold leading-none text-white"
                        >
                            {nonLues > 99 ? '99+' : nonLues}
                        </span>
                    )}
                </span>
            </IconButton>

            <Text variante="sousTitre" as="h1" className="min-w-0 flex-1 truncate">
                {titre}
            </Text>

            {action}
        </header>
    );
}
